import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { AppRoutingModule } from './app.routing';
import { AuthGuard } from './_guards/auth.guard';
import { ModalAuthGuard } from './_guards/modal-auth.guard';
import { CookieConsentBannerComponent } from './components/cookie-consent-banner/cookie-consent-banner.component';

import { FormsModule }    from '@angular/forms';
import { JwtModule } from '@auth0/angular-jwt';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AppCommonPipesModule } from './app-common-pipes/app-common-pipes.module';
import { SmsSharedModule } from './sms-shared/sms-shared.module';
import { PeopleModule } from './components/people/people.module';
import { HorsesModule } from './components/horses/horses.module';
import { EntryAccountStatementModule } from './components/entry-account-statement/entry-account-statement.module';

import { AppComponent } from './app.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { ModalChooseFederationComponent } from './components/modal-choose-federation/modal-choose-federation.component';

import { AppSettings } from './app.settings';
import { readAuthPackage } from './providers/auth-token';

//SERVICE PROVIDERS
import { SessionService } from './providers/session.service';
import { HorseShowService } from './providers/horse-show.service';
import { SmsApiSessionService } from './providers/sms-api-session.service';
import { UserService } from './providers/user.service';
import { CreateUserComponent } from './components/create-user/create-user.component';
import { HorseShowEntryService } from './providers/horse-show-entry.service';
import { PendingHorseShowEntryService } from './providers/pending-horse-show-entry.service';
import { HorseShowWithEntriesService } from './providers/horse-show-with-entries.service';
import { UsefHorseSearchService } from './providers/usef-horse-search.service';
import { UsefPersonSearchService } from './providers/usef-person-search.service';
import { UsefPersonService } from './providers/usef-person.service';
import { HorseService } from './providers/horse.service';
import { PersonService } from './providers/person.service';
import { WaiverService } from './providers/waiver.service';
import { VersionCheckService } from './providers/version-check.service';
import { PaymentCustomerService } from './providers/payment-customer.service';
import { PaymentCardService } from './providers/payment-card.service';
import { StripePaymentCardService } from './providers/stripe-payment-card.service';
import { RefundPolicyService } from './providers/refund-policy.service';
import { CheckoutRequestService } from './providers/checkout-request.service';
import { CheckoutService } from './providers/checkout.service';
import { BankingService } from './providers/banking.service';
import { SubmitEntriesService } from './providers/submit-entries.service';
import { NominatingFeeService } from './providers/nominating-fee.service';
import { ConvenienceFeeService } from './providers/convenience-fee.service';
import { RiderNominationService } from './providers/rider-nomination.service';
import { BarnAccountService } from './providers/barn-account.service';
import { VenueService } from './providers/venue.service';
import { OrganizerService } from './providers/organizer.service';
import { QualifyingInvitationService } from './providers/qualifying-invitation.service';
import { CompetitionEntryService } from './providers/competition-entry.service';
import { EntryFeeService } from './providers/entry-fee.service';
import { QualifyingEntryStatementService } from './providers/qualifying-entry-statement.service';
import { SubmitAcceptanceService } from './providers/submit-acceptance.service';
import { EntryTaskService } from './providers/entry-task.service';
import { HorseShowEntryWithTypesService } from './providers/horse-show-entry-with-types.service';
import { RiderInvitationCheckService } from './providers/rider-invitation-check.service';
import { PublicRecordsService } from './providers/public-records.service';
import { BiosecurityProtocolService } from './providers/biosecurity-protocol.service';
import { ElectronicVetService } from './providers/electronic-vet.service';
import { SearchHorseShowEntryService } from './providers/search-horse-show-entry.service';
import { HorseShowArenaService } from './providers/horse-show-arena.service';
import { DocumentService } from './providers/document.service';
import { DocumentSignatureRequestService } from './providers/document-signature-request.service';
import { HorseShowSeriesService } from './providers/horse-show-series.service';
import { HorseShowsInSeriesService } from './providers/horse-shows-in-series.service';
import { PusherService } from './providers/pusher.service';
import { AccountStatusService } from './providers/account-status.service';
import { CircuitService } from './providers/circuit.service';
import { CopyEntryForCircuitService } from './providers/copy-entry-for-circuit.service';
import { AdditionalWaiverIdsService } from './providers/additional-waiver-ids.service';
import { EntryBuilderService } from './providers/entry-builder.service';
import { FixedInventoryService } from './providers/fixed-inventory.service';
import { CouponService } from './providers/coupon.service';

import { ModalChooseSectionComponent } from './components/modal-choose-section/modal-choose-section.component';
import { ModalChooseCompetitionComponent } from './components/modal-choose-competition/modal-choose-competition.component';
import { MyEntriesComponent } from './components/my-entries/my-entries.component';
import { StablingFilterPipe } from './my-pipes/stabling-filter.pipe';
import { ModalReloadAppComponent } from './components/modal-reload-app/modal-reload-app.component';
import { LoggedInComponent } from './components/logged-in/logged-in.component';
import { MyAccountComponent } from './components/my-account/my-account.component';

import { ModalConfirmDialogComponent } from './components/modal-confirm-dialog/modal-confirm-dialog.component';
import { ModalEditHorseOnEntryComponent } from './components/modal-edit-horse-on-entry/modal-edit-horse-on-entry.component';
import { ModalEditPersonOnEntryComponent } from './components/modal-edit-person-on-entry/modal-edit-person-on-entry.component';
import { ModalChooseRiderComponent } from './components/modal-choose-rider/modal-choose-rider.component';
import { ModalEditCompetitionsComponent } from './components/modal-edit-competitions/modal-edit-competitions.component';
import { ModalViewPendingEntryComponent } from './components/modal-view-pending-entry/modal-view-pending-entry.component';
import { ViewEntryFormComponent } from './components/view-entry-form/view-entry-form.component';
import { ViewSignatureComponent } from './components/view-signature/view-signature.component';

import { ModalEditPayeeOnEntryComponent } from './components/modal-edit-payee-on-entry/modal-edit-payee-on-entry.component';
import { ModalEditBillingItemsComponent } from './components/modal-edit-billing-items/modal-edit-billing-items.component';
import { ModalEditSignaturesOnEntryComponent } from './components/modal-edit-signatures-on-entry/modal-edit-signatures-on-entry.component';
import { ModalEditMiscOnEntryComponent } from './components/modal-edit-misc-on-entry/modal-edit-misc-on-entry.component';
import { ModalPayBalanceComponent } from './components/modal-pay-balance/modal-pay-balance.component';
import { SubmitEntriesComponent } from './components/submit-entries/submit-entries.component';
import { EntriesReceiptComponent } from './components/entries-receipt/entries-receipt.component';
import { PrintEntryFormComponent } from './components/print-entry-form/print-entry-form.component';
import { PaymentReceiptComponent } from './components/payment-receipt/payment-receipt.component';
import { ModalPaymentReceiptComponent } from './components/modal-payment-receipt/modal-payment-receipt.component';
import { AdoptIdentityLoginComponent } from './components/adopt-identity-login/adopt-identity-login.component';
import { OrganizerComponent } from './components/organizer/organizer.component';
import { SubmitSignatureComponent } from './components/submit-signature/submit-signature.component';
import { ModalEnterAccessCodeComponent } from './components/modal-enter-access-code/modal-enter-access-code.component';
import { StorefrontComponent } from './components/storefront/storefront.component';
import { StorefrontReceiptComponent } from './components/storefront/storefront-receipt/storefront-receipt.component';
import { EditPayeeForHorseShowComponent } from './components/edit-payee-for-horse-show/edit-payee-for-horse-show.component';
import { PayeeOtherEntriesComponent } from './components/payee-other-entries/payee-other-entries.component';
import { ModalLoginComponent } from './components/modal-login/modal-login.component';
import { EditPayeeCompleteComponent } from './components/edit-payee-for-horse-show/edit-payee-complete/edit-payee-complete.component';
import { ModalEntrySearchComponent } from './components/modal-entry-search/modal-entry-search.component';
import { SignDocumentComponent } from './components/sign-document/sign-document.component';
import { LostPasswordComponent } from './components/lost-password/lost-password.component';
import { CredentialingComponent } from './components/credentialing/credentialing.component';
import { AccountStatementListComponent } from './components/account-statement-list/account-statement-list.component';
import { ViewAccountStatementComponent } from './components/account-statement-list/view-account-statement/view-account-statement.component';
import { ModalSelectCircuitShowsComponent } from './components/modal-select-circuit-shows/modal-select-circuit-shows.component';
import { ModalCopyCircuitEntryComponent } from './components/modal-copy-circuit-entry/modal-copy-circuit-entry.component';
import { EnterHorseShowLandingComponent } from './components/enter-horse-show-landing/enter-horse-show-landing.component';
import { DeclarationsLandingComponent } from './components/declarations-landing/declarations-landing.component';
import { CompetitionNameSearchPipe } from './my-pipes/competition-name-search.pipe';
import { PrintAccountStatementComponent } from './components/account-statement-list/print-account-statement/print-account-statement.component';
import { PrintableEntryAccountStatementComponent } from './components/printable-entry-account-statement/printable-entry-account-statement.component';
import { EntriesChequeReceiptComponent } from './components/entries-cheque-receipt/entries-cheque-receipt.component';
import { UpdatePasswordComponent } from './components/update-password/update-password.component';
import { LostPasswordService } from './providers/lost-password.service';
import { EntriesReceiptWithoutPaymentComponent } from './components/entries-receipt-without-payment/entries-receipt-without-payment.component';
import { AgreementAssentComponent } from './components/agreement-assent/agreement-assent.component';
import { AgreementService } from './providers/agreement.service';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LoginComponent,
    CreateUserComponent,
    ModalChooseFederationComponent,
    ModalChooseSectionComponent,
    ModalChooseCompetitionComponent,
    MyEntriesComponent,
    StablingFilterPipe,
    ModalReloadAppComponent,
    LoggedInComponent,
    MyAccountComponent,
    ModalConfirmDialogComponent,
    ModalEditHorseOnEntryComponent,
    ModalEditPersonOnEntryComponent,
    ModalChooseRiderComponent,
    ModalEditCompetitionsComponent,
    ModalViewPendingEntryComponent,
    ViewEntryFormComponent,
    ViewSignatureComponent,
    ModalEditPayeeOnEntryComponent,
    ModalEditBillingItemsComponent,
    ModalEditSignaturesOnEntryComponent,
    ModalPayBalanceComponent,
    ModalEditMiscOnEntryComponent,
    SubmitEntriesComponent,
    EntriesReceiptComponent,
    PrintEntryFormComponent,
    PaymentReceiptComponent,
    ModalPaymentReceiptComponent,
    AdoptIdentityLoginComponent,
    OrganizerComponent,
    SubmitSignatureComponent,
    ModalEnterAccessCodeComponent,
    StorefrontComponent,
    StorefrontReceiptComponent,
    EditPayeeForHorseShowComponent,
    PayeeOtherEntriesComponent,
    ModalLoginComponent,
    EditPayeeCompleteComponent,
    ModalEntrySearchComponent,
    SignDocumentComponent,
    LostPasswordComponent,
    CredentialingComponent,
    AccountStatementListComponent,
    ViewAccountStatementComponent,
    ModalSelectCircuitShowsComponent,
    ModalCopyCircuitEntryComponent,
    EnterHorseShowLandingComponent,
    DeclarationsLandingComponent,
    CompetitionNameSearchPipe,
    PrintAccountStatementComponent,
    PrintableEntryAccountStatementComponent,
    EntriesChequeReceiptComponent,
    UpdatePasswordComponent,
    EntriesReceiptWithoutPaymentComponent,
    AgreementAssentComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    CommonModule,
    FormsModule,
    AppRoutingModule,
    AppCommonPipesModule,
    SmsSharedModule,
    PeopleModule,
    HorsesModule,
    EntryAccountStatementModule,
    FontAwesomeModule,
    NgbModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        allowedDomains: ['entries.showmanagementsystem.com', 'sandbox.showmanagementsystem.com', 'entries.jonkassel.net', 'dev.electronicvet.com', 'localhost:1976', 'localhost:5002', 'api-v2.showmanagementsystem.com', 'ecommerce-api.showmanagementsystem.com'],
        disallowedRoutes: []
      }
    }),
    CookieConsentBannerComponent
  ],
  providers: [
  	AuthGuard,
    ModalAuthGuard,
  	SessionService,
  	HorseShowService,
    HorseShowEntryService,
    PendingHorseShowEntryService,
    HorseShowWithEntriesService,
  	SmsApiSessionService,
  	UserService,
    UsefHorseSearchService,
    UsefPersonSearchService,
    UsefPersonService,
    HorseService,
    PersonService,
    WaiverService,
    VersionCheckService,
    PaymentCustomerService,
    StripePaymentCardService,
    PaymentCardService,
    RefundPolicyService,
    CheckoutRequestService,
    BankingService,
    SubmitEntriesService,
    CheckoutService,
    NominatingFeeService,
    ConvenienceFeeService,
    RiderNominationService,
    BarnAccountService,
    VenueService,
    OrganizerService,
    QualifyingInvitationService,
    CompetitionEntryService,
    EntryFeeService,
    QualifyingEntryStatementService,
    SubmitAcceptanceService,
    EntryTaskService,
    HorseShowEntryWithTypesService,
    RiderInvitationCheckService,
    PublicRecordsService,
    BiosecurityProtocolService,
    ElectronicVetService,
    SearchHorseShowEntryService,
    HorseShowArenaService,
    DocumentService,
    DocumentSignatureRequestService,
    HorseShowSeriesService,
    HorseShowsInSeriesService,
    PusherService,
    AccountStatusService,
    CircuitService,
    CopyEntryForCircuitService,
    AdditionalWaiverIdsService,
    EntryBuilderService,
    FixedInventoryService,
    CouponService,
    LostPasswordService,
    AgreementService
  ],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ],
  exports: [
    AppCommonPipesModule,
    FontAwesomeModule
  ],
  bootstrap: [AppComponent]
})

export class AppModule { }

export function tokenGetter() {
  // readAuthPackage tolerates missing or malformed storage; a bare JSON.parse
  // here would throw on corrupt storage and take the whole app down at boot.
  const auth_package = readAuthPackage();
  return (auth_package ? auth_package.token : "");
}
